import { ReactNode, useState } from 'react';

import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { ALT_TEXT_FIELD } from 'approot/shared/stimulus/form-fields/stimulus-form-fields.constants';
import {
  SettingSmallText,
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import type {
  GenerateImageAltTextQuery,
  GenerateImageAltTextQueryVariables,
} from '../../../../dictionary/dictionary.graphql.generated';
import { GENERATE_IMAGE_ALT_TEXT } from 'approot/dictionary/dictionary.graphql';
import AutoResizingTextArea from 'approot/shared/auto-resizing-textarea/auto-resizing-textarea';
import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import ComponentImageEditor from './component-image-editor';
import { FILL, FIT, FlipMode, Mode } from '../../image/image-crop.constants';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import { Image, ImageCropData } from './component-image-modal.constants';
import { Box } from 'approot/shared/design-system/box/box.style';
import ComponentImageSource from './component-image-source';
import ComponentAltTextTooltip from '../component-alt-text/component-alt-text-tooltip';
import { ALT_TEXT_MAX } from '../component-alt-text/component-alt-text.constants';

type Props = {
  loading?: boolean;
  containerDimensions: {
    width: number;
    height: number;
  };
  image?: Image | null;
  cropData?: ImageCropData | null;
  sourceUrl?: string | null;
  altText?: string | null;
  canFit?: boolean;
  canFill?: boolean;
  canFlip?: boolean;
  canAddSource?: boolean;
  canDelete?: boolean;
  canUpdateAltText?: boolean;
  isImageRequired?: boolean;
  extraContent?: ReactNode;
  closeModal: () => void;
  onUpdateData: (props: {
    altText: string | null;
    cropData: ImageCropData | null;
    imageLayout: Mode;
    image?: Image | null;
    sourceUrl: string | null;
  }) => void;
};

const ComponentImageModal = ({
  loading,
  containerDimensions,
  image: initialImage,
  cropData: initialCropData,
  sourceUrl: initialSourceUrl,
  altText,
  canFill = true,
  canFit = true,
  canFlip = true,
  canDelete = true,
  canAddSource = true,
  canUpdateAltText = true,
  isImageRequired,
  extraContent,
  closeModal,
  onUpdateData,
}: Props) => {
  const [sourceUrl, setSourceUrl] = useState<string>(initialSourceUrl || '');
  const [alt, setAlt] = useState<string>(altText?.substring(0, 1000) || '');
  const [mode, setMode] = useState<Mode>(!initialCropData ? FIT : FILL);
  const [image, setImage] = useState<Image | null | undefined>(initialImage);
  const [cropData, setImageCropData] = useState<ImageCropData | null>(
    initialCropData || null
  );
  const [error, setError] = useState<string>();

  const { showError } = useSnackbar();

  const [generateAltText, { loading: altTextLoading }] = useLoggingLazyQuery<
    GenerateImageAltTextQuery,
    GenerateImageAltTextQueryVariables
  >(GENERATE_IMAGE_ALT_TEXT, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      const altText = res.generateImageAltText.altText.substring(0, 1000);
      setAlt(altText);
      const altTextElement = document.getElementById(ALT_TEXT_FIELD);
      altTextElement?.focus();
    },
    onError: () => {
      setAlt(alt);
      showError(
        "Sorry, we couldn't generate the alt text this time. Please try again."
      );
    },
  });

  return (
    <>
      <ActivityBuilderModal
        isVisible
        size={640}
        title="Edit Image"
        headerTitle="Edit Image"
        onClose={closeModal}
      >
        <FileDropperContextProvider>
          <SettingsGroup>
            <ComponentImageEditor
              imageDimensions={containerDimensions}
              imageFileName={image?.fileName}
              imageKey={image?.key}
              imageUrl={image?.original}
              flipValue={(image?.data?.flip as FlipMode) || null}
              canFill={canFill}
              canFit={canFit}
              canFlip={canFlip}
              error={error}
              cropValue={cropData}
              onFlipImage={flip => {
                if (image) {
                  setImage({
                    ...image,
                    data: {
                      flip,
                    },
                  });
                }
              }}
              onDeleteImage={canDelete ? () => setImage(undefined) : undefined}
              onUploadImage={newImage => {
                setImage({
                  ...image,
                  ...newImage,
                  data: {
                    flip: null,
                  },
                });
              }}
              onCropChange={(values, mode) => {
                setMode(mode);
                setImageCropData(values);
              }}
            />
          </SettingsGroup>

          {canAddSource && (
            <ComponentImageSource
              sourceUrl={sourceUrl}
              onChange={setSourceUrl}
            />
          )}

          <SettingsGroup marginBottom={0}>
            <SettingsLabel htmlFor={ALT_TEXT_FIELD}>
              <Box $justifyContent="space-between" $marginBottom={0}>
                <span>
                  Alt Text
                  <ComponentAltTextTooltip />
                </span>
                <SettingSmallText marginBottom={0}>Optional</SettingSmallText>
              </Box>
            </SettingsLabel>
            <AutoResizingTextArea
              showCounter
              rows={3}
              maxLength={ALT_TEXT_MAX}
              id={ALT_TEXT_FIELD}
              name={ALT_TEXT_FIELD}
              placeholder={
                altTextLoading
                  ? 'Generating Alt Text...'
                  : 'Describe this image'
              }
              initialValue={alt}
              disabled={!canUpdateAltText || loading || altTextLoading}
              onChange={e => setAlt(e.target.value)}
              data-selector="txt-describe-image"
            />
          </SettingsGroup>
          {extraContent}
          <SimpleModalVerticalActionsStyled>
            {canUpdateAltText && (
              <SecondaryLoadingButton
                data-selector="generate-alt-text"
                isLoading={altTextLoading}
                isDisabled={loading || altTextLoading || !image}
                onClick={() => {
                  setAlt('');
                  generateAltText({
                    variables: {
                      input: {
                        imageUrl: image?.original || '',
                      },
                    },
                  });
                }}
              >
                {altTextLoading
                  ? 'Generating Alt Text'
                  : 'Generate Alt Text ✨'}
              </SecondaryLoadingButton>
            )}

            <PrimaryButton
              type="button"
              loading={loading}
              disabled={loading || altTextLoading}
              onClick={() => {
                if (isImageRequired && !image) {
                  setError(`Oops! Don't forget to upload an image here.`);
                } else {
                  setError('');
                  onUpdateData({
                    altText: alt,
                    cropData,
                    imageLayout: mode,
                    image,
                    sourceUrl,
                  });
                }
              }}
              data-selector="btn-save-image-data"
            >
              Save
            </PrimaryButton>
          </SimpleModalVerticalActionsStyled>
        </FileDropperContextProvider>
      </ActivityBuilderModal>
    </>
  );
};

export default ComponentImageModal;
